/**
 * variant-picker-require-selection.js
 * ------------------------------------
 * Requires customers to explicitly pick a variant before Add to Cart works.
 *
 * STRATEGY:
 *   Rather than fighting Prestige's radio/label management, we let Prestige do
 *   its thing completely. We intercept the form submit and block it until the
 *   customer has made at least one explicit click on a variant option.
 *
 *   - ATC buttons are disabled on load (visual lock)
 *   - Form submit is also intercepted as a safety net
 *   - First radio change inside the picker = customer made a choice = unlock
 *
 * IMPORTANT: Prestige renders the actual option list (the `<label
 * class="popover__value-option">` elements) inside a separate popover/dialog
 * that is NOT a DOM descendant of <variant-picker> — it's only linked back to
 * the picker via aria-controls on the trigger button. So option clicks are
 * handled with a single document-level delegated listener, and we resolve
 * which picker "owns" a given option tap by mapping the popover's id back to
 * the trigger button. This also protects against <x-popover> using Shadow
 * DOM (we walk e.composedPath() rather than relying on e.target.closest()).
 *
 * HOW TO INSTALL:
 *   1. Upload this file to Assets
 *   2. Add to layout/theme.liquid before </body>:
 *      <script src="{{ 'variant-picker-require-selection.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  const PICKER_ATTR = 'data-require-selection';
  const LOCK_ATTR   = 'data-selection-locked';
  const PICKER_SEL  = `variant-picker[${PICKER_ATTR}]`;

  // picker element -> { positions:Set, chosen:Set, chosenLabels:Map, formId, form }
  const pickerState = new Map();

  /**
   * Get all ATC submit buttons for this picker's form.
   * Prestige renders two: one inside the <form>, one outside with form="id".
   */
  function getAtcButtons(formId, form) {
    if (!formId) return [];
    const set = new Set();
    document
      .querySelectorAll(`button[form="${formId}"][type="submit"], input[form="${formId}"][type="submit"]`)
      .forEach(b => set.add(b));
    if (form) {
      form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(b => set.add(b));
    }
    return [...set];
  }

  function lockAll(btns) {
    btns.forEach(b => { b.disabled = true; b.setAttribute(LOCK_ATTR, ''); });
  }

  function unlockAll(btns) {
    btns.forEach(b => { b.disabled = false; b.removeAttribute(LOCK_ATTR); });
  }

  function applyTriggerLabel(picker, popoverId, labelText) {
    const esc       = CSS.escape(popoverId);
    const valueSpan = picker.querySelector(`#${esc}-selected-value`);
    if (valueSpan && valueSpan.textContent.trim() !== labelText) {
      valueSpan.textContent = labelText;
    }
    const trigger = picker.querySelector(`[aria-controls="${esc}"]`);
    if (trigger) {
      trigger.classList.remove('select--unselected');
      trigger.classList.add('select--selected');
    }
  }

  function markChosen(picker, state, pos) {
    if (!pos) return;
    state.chosen.add(pos);
    if (state.chosen.size >= state.positions.size) {
      unlockAll(getAtcButtons(state.formId, state.form));
    }
  }

  function syncTriggerLabel(picker, state, label) {
    const popover = label.closest('x-popover');
    if (!popover || !popover.id) return;
    const labelText = label.querySelector(':scope > span')?.textContent?.trim();
    if (!labelText) return;
    state.chosenLabels.set(popover.id, labelText);
    applyTriggerLabel(picker, popover.id, labelText);
  }

  /**
   * After Prestige re-renders the picker (e.g. auto-selecting a compatible
   * size when the color changes), pick up any position that now has a
   * checked, usable radio — even though no click/change event fired for it,
   * since Prestige sets `checked` directly when it swaps the markup.
   *
   * Only called from the re-render path (reapply), never on initial load —
   * a pre-checked default variant on first paint should NOT count as an
   * explicit choice, that's the whole point of this script.
   */
  function syncFromCheckedRadios(picker, state) {
    state.positions.forEach(pos => {
      if (state.chosen.has(pos)) return;
      const checked = picker.querySelector(
        `input[type="radio"][data-option-position="${pos}"]:checked`
      );
      if (!checked || checked.disabled || checked.closest('label')?.classList.contains('is-disabled')) return;
      const label = checked.closest('label.popover__value-option');
      markChosen(picker, state, pos);
      if (label) syncTriggerLabel(picker, state, label);
    });
  }

  /**
   * Given a click/change event that originated on (or inside) a variant
   * option, find which <variant-picker> it belongs to.
   *
   * Handles two DOM shapes:
   *   1. The option genuinely lives inside <variant-picker> (some themes).
   *   2. The option lives in a portalled popover/dialog elsewhere in the DOM
   *      (Prestige), linked back only via aria-controls on the trigger.
   */
  function resolvePicker(node) {
    let picker = node.closest?.(PICKER_SEL);
    if (picker) return picker;

    const popover = node.closest?.('x-popover');
    if (!popover || !popover.id) return null;

    const trigger = document.querySelector(`[aria-controls="${CSS.escape(popover.id)}"]`);
    return trigger ? trigger.closest(PICKER_SEL) : null;
  }

  /** Walk composedPath (handles Shadow DOM retargeting) for the first element matching selector. */
  function findInPath(e, selector) {
    const path = typeof e.composedPath === 'function' ? e.composedPath() : [e.target];
    for (const el of path) {
      if (el && el.nodeType === 1 && el.matches?.(selector)) return el;
    }
    return null;
  }

  function handleOptionInteraction(label, radio) {
    if (!label && !radio) return;
    const picker = resolvePicker(radio || label);
    if (!picker) return;
    const state = pickerState.get(picker);
    if (!state) return;

    const pos = radio?.getAttribute('data-option-position');
    markChosen(picker, state, pos);
    if (label) syncTriggerLabel(picker, state, label);
  }

  // Single delegated listeners on document, capture phase, registered once.
  document.addEventListener('click', e => {
    const label = findInPath(e, 'label.popover__value-option');
    if (!label) return;
    const radio = label.querySelector('input[type="radio"][data-option-position]');
    handleOptionInteraction(label, radio);
  }, true);

  document.addEventListener('change', e => {
    const radio = findInPath(e, 'input[type="radio"][data-option-position]');
    if (!radio) return;
    const label = radio.closest('label.popover__value-option');
    handleOptionInteraction(label, radio);
  }, true);

  function initPicker(picker) {
    if (picker.dataset.requireSelectionInit) return;
    picker.dataset.requireSelectionInit = '1';

    const formId = picker.getAttribute('form-id');
    const form   = document.getElementById(formId);

    const positions = new Set(
      [...picker.querySelectorAll('input[type="radio"][data-option-position]')]
        .map(r => r.getAttribute('data-option-position'))
    );

    const state = {
      formId,
      form,
      positions,
      chosen: new Set(),
      chosenLabels: new Map(),
    };
    pickerState.set(picker, state);

    lockAll(getAtcButtons(formId, form));

    // Re-apply our label + lock state after Prestige replaces picker innerHTML.
    const reapply = () => {
      syncFromCheckedRadios(picker, state);
      state.chosenLabels.forEach((text, id) => applyTriggerLabel(picker, id, text));
      if (state.chosen.size >= state.positions.size) {
        unlockAll(getAtcButtons(state.formId, state.form));
      } else {
        getAtcButtons(state.formId, state.form)
          .filter(b => !b.hasAttribute(LOCK_ATTR))
          .forEach(b => { b.disabled = true; b.setAttribute(LOCK_ATTR, ''); });
      }
    };
    new MutationObserver(reapply).observe(picker, { childList: true, subtree: true });

    // Auto-mark positions with only one purchasable option.
    positions.forEach(pos => {
      const usable = [...picker.querySelectorAll(`input[type="radio"][data-option-position="${pos}"]`)]
        .filter(r => !r.closest('label')?.classList.contains('is-disabled') && !r.disabled);
      if (usable.length === 1) markChosen(picker, state, pos);
    });

    // If URL already has ?variant=, the customer either arrived via a deep link
    // or Prestige just re-rendered after a selection. Either way, treat the
    // pre-checked radios as deliberate choices and sync the trigger labels.
    if (new URLSearchParams(location.search).has('variant')) {
      picker.querySelectorAll('input[type="radio"][data-option-position]:checked')
        .forEach(radio => {
          const label = radio.closest('label.popover__value-option');
          if (!label) return;
          markChosen(picker, state, radio.getAttribute('data-option-position'));
          syncTriggerLabel(picker, state, label);
        });
    }

    if (form) {
      form.addEventListener('submit', e => {
        if (state.chosen.size < state.positions.size) {
          e.preventDefault();
          e.stopImmediatePropagation();
          picker.querySelectorAll('fieldset').forEach(fieldset => {
            const pos = fieldset.querySelector('input[type="radio"][data-option-position]')
              ?.getAttribute('data-option-position');
            if (pos && !state.chosen.has(pos)) {
              const trigger = fieldset.querySelector('.select');
              if (trigger) {
                trigger.classList.add('select--error');
                setTimeout(() => trigger.classList.remove('select--error'), 2000);
              }
            }
          });
        }
      }, true);
    }
  }

  function init() {
    document.querySelectorAll(PICKER_SEL).forEach(initPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  // Catch <variant-picker> elements Prestige replaces in-place after variant changes.
  // shopify:section:load doesn't fire for these — only a MutationObserver catches them.
  new MutationObserver(muts => {
    for (const m of muts) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.(PICKER_SEL)) initPicker(node);
        node.querySelectorAll?.(PICKER_SEL).forEach(initPicker);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();